import React, { PropsWithChildren, useEffect, useState } from "react";
// @ts-ignore import/no-extraneous-dependencies
import { format } from "date-fns-tz";
import { Container, FlexContainer, Spacing } from "manifest-components/src/Containers";
import CarouselSlide from "manifest-components/src/Carousel/carouselSlide";
import { ReactComponent as SVGLocation } from "manifest-components/src/Images/icons/homepage/location.svg";
import { ReactComponent as SVGSecure } from "manifest-components/src/Images/icons/homepage/secure.svg";
import { ReactComponent as SVGVerified } from "manifest-components/src/Images/icons/homepage/verified.svg";
import CarouselSwiper from "manifest-components/src/Carousel/CarouselSwiper";
import MoneyPlant from "manifest-components/src/Images/illustrations/Money-Plant trimmed.png";
import SOCLogo from "manifest-components/src/Images/illustrations/SOC-logo.png";

import { Button } from "manifest-components/src/Button";

import { ColorClassNames } from "manifest-components/src/Colors";
import LogoCarousel from "manifest-components/src/LogoCarousel";
import { Popup } from "manifest-components/src/Popup";
import TestimonyCarousel from "manifest-components/src/TestimonyCarousel";
import type { WelcomeControllerType } from "../../WelcomeController";
import Header from "../../../../components/HeaderComponent";
import HeaderPlaceholder from "../../../../components/HeaderComponent/HeaderPlaceholder";
import FooterSection from "./sections/Footer";
import ConfirmEmployerScreen from "../../../RedirectScreens/ConfirmEmployerScreen/ConfirmEmployerScreen";
import WelcomeContainer from "./WelcomeContainer";
import useBreakpoints from "../../../../hooks/useBreakpoints";

import Analytics, { HeapEventAction } from "../../../../utils/analytics";

import * as Styled from "../../styles";
import { logoSlidesInfo, slidesInfo, testimonies } from "../constants";

export type WelcomeScreenProps = WelcomeControllerType & {
  isDefault?: boolean;
  logoUrl?: string;
  carouselSlides?: CarouselSlide[];
};

export default function WelcomeScreen(props: PropsWithChildren<WelcomeScreenProps>) {
  const {
    confirmEmployer,
    logoUrl,
    employerInfo,
    setConfirmEmployer,
    children,
    WelcomePopup,
    isPrelaunch,
    vendorName,
    loading,
  } = props;
  const { visible, initLayout, revertLayout } = useBreakpoints({
    desktopWidth: "1180px",
    mobileSizes: [950],
  });

  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= Styled.welcomeScreenBreakpoint);
  const [showTolstoyPopup, setShowTolstoyPopup] = useState<boolean>(false);

  useEffect(() => {
    const getBreakpoint = () => {
      setIsDesktop(window.innerWidth >= Styled.welcomeScreenBreakpoint);
    };
    window.addEventListener("resize", getBreakpoint);
    return () => window.removeEventListener("resize", getBreakpoint);
  }, []);

  const login = () => {
    revertLayout();
    props.login();
  };

  const createAccount = (buttonName?: any) => {
    Analytics.track({
      action: HeapEventAction.clicked,
      buttonName: buttonName || "Get Started",
      screen: `${employerInfo?.companyName ? `${employerInfo?.companyName} welcome page` : "Welcome page"}`,
    });
    Analytics.clickSignUp({ vendor: employerInfo.companyName || "" });
    revertLayout();
    props.createAccount();
  };

  const goToAccountCreationScreen = (isCurrentEmployer: boolean) => () => {
    revertLayout();
    props.goToAccountCreationScreen(isCurrentEmployer)();
  };

  const handleLogoClicked = () => {
    initLayout();
    setConfirmEmployer(false);
  };

  useEffect(() => {
    if (loading) return;
    Analytics.track({
      action: HeapEventAction.landed,
      screen: !employerInfo?.companyName
        ? "Manifest B2C Welcome page - no vendor"
        : `${employerInfo?.companyName} Welcome Page`,
    });
    Analytics.landOnWelcomePage({ vendor: vendorName || "" });
  }, [loading, vendorName]);

  if (!visible || loading) return null;

  const renderContent = () => {
    if (confirmEmployer) {
      return (
        <FlexContainer $justifyContent="center" margin={100} maxWidth={600}>
          <ConfirmEmployerScreen
            employerName={employerInfo.companyName}
            goToAccountCreationScreen={goToAccountCreationScreen}
            goBackHome={() => setConfirmEmployer(false)}
          />
        </FlexContainer>
      );
    }
    if (children) return children;
    return (
      <Styled.NewPageLayout>
        <Container flex={1} className="tile-section-c welcomeScreenContentContainer">
          <Styled.NewPageLayoutHeroSection>
            <h1>
              Seamlessly transfer <br />
              your old retirement accounts – for free.
            </h1>
            <Spacing $space={24} />
            {isPrelaunch && employerInfo.launchDate && (
              <>
                <p className="large bold">
                  Manifest will be available to you on{" "}
                  {format(new Date(employerInfo.launchDate.replace("Z", "")), "MMMM d, yyyy", {
                    timeZone: "UTC",
                  })}
                  .{" "}
                </p>
                <Spacing $space={24} />
              </>
            )}
            <p className="with-padding">
              {employerInfo.companyName
                ? `To save you time and effort, ${employerInfo.companyName} has teamed up with Manifest to offer you a free tool to consolidate your old retirement accounts.`
                : "Don’t lose track of your savings. Manifest will guide you through the process for free and in less than 30 minutes."}
            </p>

            {!isPrelaunch && (
              <>
                {" "}
                <Spacing $mobileSpace={26} $laptopSpace={72} />
                <Button
                  name="signUp"
                  onClick={() => createAccount("Get started")}
                  text="Get started"
                  className="pehp-launch-button"
                />
              </>
            )}
          </Styled.NewPageLayoutHeroSection>
          <Styled.WelcomePageCarouselDiv>
            <CarouselSwiper slidesInfo={slidesInfo} showQuote={false} />
          </Styled.WelcomePageCarouselDiv>
          <Spacing $mobileSpace={40} />
        </Container>
        <Styled.WelcomePageLogoCarouselDiv>
          <p className={`${ColorClassNames.sugar} center-text bold`}>Trusted by participants at</p>
          <Spacing $space={12} />
          <div className="logo-carousel-div">
            <LogoCarousel logos={logoSlidesInfo} />
          </div>
        </Styled.WelcomePageLogoCarouselDiv>
        <Styled.WelcomeHowItWorks>
          <div className="content-div">
            <h3>How it works</h3>
            <Spacing $space={20} />
            <p className="description">
              You don't need to prepare anything to start using Manifest. Access our web application on your computer,
              tablet, or phone.
            </p>
            {!isDesktop && (
              <>
                <Spacing $mobileSpace={40} />
                <Styled.IconAndContentSection>
                  <div>
                    <SVGLocation />
                    <p>We help you locate all your old accounts—even if you don’t remember where they are.</p>
                  </div>
                  <div>
                    <SVGSecure />
                    <p>We run 25+ checks to avoid unexpected risks before initiating your transfer.</p>
                  </div>
                  <div>
                    <SVGVerified />
                    <p>We assign a dedicated transfer specialist to keep you posted on your transfer progress.</p>
                  </div>
                </Styled.IconAndContentSection>
                <Spacing $mobileSpace={40} />
              </>
            )}

            <Popup
              showContactInfo={false}
              width="600px"
              handlePopup={() => setShowTolstoyPopup(false)}
              showPopup={showTolstoyPopup}
              showHeader={false}
              content={
                showTolstoyPopup ? (
                  <Styled.TolstoyPopup>
                    <div id="tolstoy-container">
                      <iframe id="tolstoy" src="https://player.gotolstoy.com/icqdhv31pyjxg" title="tolstoy" />
                    </div>
                  </Styled.TolstoyPopup>
                ) : (
                  <></>
                )
              }
            />
            <Button
              name="learnMoreAboutManifest"
              text="Learn more about Manifest"
              onClick={() => setShowTolstoyPopup(true)}
            />
          </div>
          {isDesktop && (
            <div className="list-div">
              <Styled.IconAndContentSection>
                <div>
                  <SVGLocation />
                  <p>We help you locate all your old accounts—even if you don’t remember where they are.</p>
                </div>
                <div>
                  <SVGSecure />
                  <p>We run 25+ checks to avoid unexpected risks before initiating your transfer.</p>
                </div>
                <div>
                  <SVGVerified />
                  <p>We assign a dedicated transfer specialist to keep you posted on your transfer progress.</p>
                </div>
              </Styled.IconAndContentSection>
            </div>
          )}
        </Styled.WelcomeHowItWorks>
        <Styled.WelcomeSection>
          <Styled.WelcomeBackgrounded>
            <div className="section__content">
              <h3>It will always be free for you.</h3>
              <Spacing $space={20} />
              <p className="description">
                {employerInfo?.companyName
                  ? `Manifest has consolidated millions of dollars in assets, at no cost to individuals. Manifest partnered with ${employerInfo.companyName} to ensure that you can transfer your old retirement accounts, for free. `
                  : `Manifest has consolidated millions of dollars in assets, at no cost to individuals. Manifest works with providers to ensure that you can transfer your old retirement accounts, for free. `}
              </p>
            </div>

            <div className="section__image section__image__plant">
              <img src={MoneyPlant} alt="plant" />
            </div>
          </Styled.WelcomeBackgrounded>
        </Styled.WelcomeSection>
        <Styled.WelcomeFullWidthSection className="testimonies">
          <TestimonyCarousel testimonies={testimonies} />
        </Styled.WelcomeFullWidthSection>
        <Styled.WelcomeSection>
          <Styled.WelcomeBackgrounded className="row-reverse">
            <div className="section__content">
              <h3>Our commitment to your data security.</h3>
              <Spacing $space={20} />
              <p>
                We're SOC 2 certified, which means we meet strict standards for handling your data securely and
                responsibly. This certification shows our deep commitment to protecting your information with the best
                industry practices.{" "}
                <a href="https://usemanifest.com/security/" target="_blank" rel="noreferrer">
                  Learn more
                </a>
                .
              </p>
            </div>
            <div className="section__image section__image__soc">
              <img src={SOCLogo} alt="SOC" />
            </div>
          </Styled.WelcomeBackgrounded>
        </Styled.WelcomeSection>

        <Styled.WelcomeSection>
          <h3 className="center-text" style={{ maxWidth: "620px" }}>
            {isPrelaunch
              ? `Check back in later to consolidate your old accounts.`
              : `Set up your transfer in minutes, and we’ll handle the rest.`}
          </h3>
          {!isPrelaunch && (
            <>
              <Spacing $mobileSpace={25} $laptopSpace={67} />
              <Button text="Get started" name="signUp" onClick={() => createAccount("Get started")} />
            </>
          )}
        </Styled.WelcomeSection>
      </Styled.NewPageLayout>
    );
  };

  return (
    <Styled.WelcomeScreen data-name="welcome-page" data-vendor={employerInfo.companyName || ""}>
      <WelcomePopup />
      {loading ? (
        <> </>
      ) : isPrelaunch ? (
        <Header shouldShowLogin={false} vendorLogo={logoUrl} />
      ) : (
        <Header
          {...props}
          onSignup={createAccount}
          onLogin={login}
          shouldShowLogin
          showSignup
          signUpText="Get started"
          onLogoClicked={handleLogoClicked}
          vendorLogo={logoUrl}
        />
      )}
      <HeaderPlaceholder />
      <WelcomeContainer $alignItems="center" $flexDirection="column" $justifyContent="center">
        {renderContent()}
      </WelcomeContainer>
      {!confirmEmployer && <FooterSection />}
    </Styled.WelcomeScreen>
  );
}
